/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import {
  accessAPI,
  Boton,
  Titulo,
  Formulario,
  Sidebar,
  TablaFiltrable
} from "../shared";

// Niveles de un permiso, por su número (ver utils/permisos.js de la API).
const NIVELES = [
  { label: "Sin acceso", severity: "danger" },
  { label: "Lectura", severity: "warning" },
  { label: "Full", severity: "success" }
];

// Opciones del dropdown de cada permiso. El value va como texto: el nivel 0
// (sin acceso) es un valor válido y como número se confundiría con vacío.
const OPCIONES_NIVEL = NIVELES.map((nivel, numero) => ({
  value: String(numero),
  etiqueta: nivel.label
}));

/**
 * Permisos de un acceso de un usuario: una tabla con todos los permisos de la
 * plataforma de ese acceso y el nivel de cada uno (también los que están sin
 * acceso), y un botón de editar que abre un sidebar con un formulario: un
 * dropdown Sin acceso / Lectura / Full por permiso.
 *
 * Dibuja su propio título; el contenedor (una Card, por ejemplo) lo pone el que
 * lo monta.
 *
 * @param {number} usuarioAccesoId acceso (Usuario_x_Acceso) cuyos permisos se gestionan
 */
export default function PermisosAcceso({ usuarioAccesoId, toast }) {
  const [permisos, setPermisos] = useState(null);
  const [editable, setEditable] = useState(false);
  const [sidebar, setSidebar] = useState(false);
  const [guardando, setGuardando] = useState(false);

  function error(respuesta, porDefecto) {
    toast?.current?.show({
      severity: "error",
      summary: "Error",
      detail: respuesta?.[0]?.msg || porDefecto,
      sticky: true
    });
  }

  useEffect(() => {
    accessAPI(
      "GET",
      `admin/usuario/acceso/${usuarioAccesoId}/permisos`,
      null,
      (respuesta) => {
        setPermisos(respuesta.permisos);
        setEditable(respuesta.editable);
      },
      (respuesta) => error(respuesta, "Error al cargar los permisos")
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuarioAccesoId]);

  // El campo de cada permiso se llama por su posición: las claves llevan
  // puntos ("admin.facturacion").
  const nombreCampo = (indice) => `permiso_${indice}`;

  function guardar(campos) {
    // Solo los que se pueden editar; el resto queda como está
    const cambios = permisos
      .map((permiso, indice) => ({
        clave: permiso.clave,
        editable: permiso.editable,
        nivel: parseInt(campos[nombreCampo(indice)])
      }))
      .filter((c) => c.editable && !Number.isNaN(c.nivel))
      .map(({ clave, nivel }) => ({ clave, nivel }));

    setGuardando(true);
    accessAPI(
      "PUT",
      `admin/usuario/acceso/${usuarioAccesoId}/permisos`,
      { permisos: cambios },
      () => {
        const nivelNuevo = new Map(cambios.map((c) => [c.clave, c.nivel]));
        setGuardando(false);
        setSidebar(false);
        setPermisos((prev) =>
          prev.map((p) =>
            nivelNuevo.has(p.clave) ? { ...p, nivel: nivelNuevo.get(p.clave) } : p
          )
        );
        toast?.current?.show({
          severity: "success",
          summary: "Actualizado",
          detail: "Permisos actualizados"
        });
      },
      (respuesta) => {
        setGuardando(false);
        error(respuesta, "Error al guardar los permisos");
      }
    );
  }

  return (
    <>
      <Titulo
        botones={
          editable &&
          permisos?.length > 0 && (
            <Boton icono negro callback={() => setSidebar(true)}>
              <i className="pi pi-pencil" />
            </Boton>
          )
        }
      >
        Permisos
      </Titulo>

      {!permisos && (
        <p css={{ color: "var(--grisTextos)" }}>Cargando permisos...</p>
      )}

      {permisos && (
        <TablaFiltrable conFiltros={false}
          value={permisos}
          size="small"
          emptyMessage="Este acceso no tiene permisos configurables"
        >
          <Column header="Permiso" field="nombre" />
          <Column header="Descripción" field="descripcion" />
          <Column
            header="Nivel de acceso"
            body={(permiso) => {
              const nivel = NIVELES[permiso.nivel];
              return <Tag severity={nivel.severity} value={nivel.label} />;
            }}
            style={{ width: "10rem" }}
          />
        </TablaFiltrable>
      )}

      <Sidebar
        visible={sidebar}
        position="right"
        onHide={() => setSidebar(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Editar permisos</Titulo>
        {/* Se monta al abrir para que arranque con los niveles actuales */}
        {sidebar && permisos && (
          <Formulario
            config={{
              secciones: [
                {
                  campos: permisos.map((permiso, indice) => ({
                    nombre: nombreCampo(indice),
                    tipoitemformularioid: 5,
                    etiqueta: permiso.nombre,
                    opciones: OPCIONES_NIVEL,
                    requerido: true,
                    sinOpcionInicial: true,
                    respuesta: String(permiso.nivel),
                    // Nadie se cambia su propio permiso de administradores
                    disabled: !permiso.editable
                  }))
                }
              ],
              botones: [
                { etiqueta: "Guardar cambios", callback: guardar, validar: true }
              ]
            }}
            loader={guardando}
          />
        )}
      </Sidebar>
    </>
  );
}
